<script>
import { preventDefault } from 'svelte/legacy';
import { onMount } from 'svelte';

import {
    authState
} from "@app/store/authStore.svelte.js";
import {
    modalState, hideModal, showModal
} from "@app/store/modalStore.svelte.js";
import {
    eventGroupState
} from "@app/store/eventGroupStore.svelte.js";
import {
    projectState
} from "@app/store/projectStore.svelte.js";
import {
    styleState
} from "@app/store/styleStore.svelte.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import removeTextlessEvents from "@cclib/functions/eventGroups/removeTextlessEvents.js";
import {
    toast } from '@zerodevx/svelte-toast';
import {
    fade
} from "svelte/transition";
import {
    BarLoader
} from "svelte-loading-spinners";
import {
    v4 as uuidv4
} from "uuid";
/* Firebase */
import { auth, functions } from '@app/configs/firebase.js';
import db from '@app/configs/firestore.js';
import storage from '@app/configs/storage.js';
import { httpsCallable } from "firebase/functions";
import { collection, doc, setDoc, Timestamp } from "firebase/firestore";
import { ref, uploadString } from "firebase/storage";
/* Language Settings */
import googleLanguages from "@cclib/providers/googleTranslate/languages.js";
import deepLLanguages from "@cclib/providers/deepL/languages.js";
import modernMtLanguages from "@cclib/providers/modernMt/languages.js";
import laraLanguages from "@cclib/providers/lara/languages.js";
import openAiLanguages from "@cclib/providers/openAi/languages.js";
import geminiProLanguages from "@cclib/providers/gemini/languages.js";
import anthropicLanguages from "@cclib/providers/anthropic/languages.js";
import azureLanguages from "@cclib/providers/azure/languages.js";
import amazonLanguages from "@cclib/providers/amazon/languages.js";
import * as m from '@app/paraglide/messages.js';
import {
    isTrialUser,
    getTrialJobSlotsRemaining,
    writeTrialLimitJob,
    TRIAL_LIMIT_MESSAGE,
} from "@app/lib/trialJobLimit.js";

let translationDefaults = JSON.parse(localStorage.getItem("cc-translation-defaults")) || {};
let provider = $state(translationDefaults.provider || "Azure AI Translator"),
    displayName = $state(eventGroupState[projectState.selected].name + " (Translation)"),
    srcLanguage = $state(translationDefaults.srcLanguage || "en"),
    selectedTargetLanguages = $state(translationDefaults.selectedTargetLanguages || [translationDefaults.trgLanguage || "fr"]),
    rtlLanguages = $state(["ar", "arc", "ckb", "dv", "fa", "ha", "he", "khw", "ks", "ps", "sd", "ur", "uz-AF", "yi", "ar-SA", "he-IL", "fa-IR", "ur-PK", "ckb-IQ", "ps-PK", "pbt-PK", "sd-PK", "ydd-US", "kas-IN", "prs-AF"]),
    formatting = $state(translationDefaults.formatting ?? true),
    waiting = $state(false),
    statusMsg = $state(""),
    progress = $state(0),
    totalJobs = $state(0),
    completedJobs = $state(0),
    eventGroupIndex = $state(projectState.selected),
    teamId = authState.team ? authState.team.id : null,
    teamOwner = authState.team ? authState.team.owner : null,
    teamOwnerId = authState.team ? authState.team.ownerId : null,
    userId = auth.currentUser.uid,
    userEmail = auth.currentUser.email,
    homeRef = authState.team ? collection(db, "teams", teamId, "jobs") : collection(db, "users", userId, "jobs"),
    storageRef = ref(storage),
    uploadBasePath = authState.team ? "teams/" + teamId + "/eventGroups/" : "users/" + userId + "/eventGroups/";

// Get the current language maps based on provider
let targetLanguageMap = $state({});

onMount(() => {
    updateTargetLanguageMap();
});

function updateTargetLanguageMap() {
    if (provider === "Google Translate") {
        targetLanguageMap = googleLanguages.target;
    } else if (provider === "Lara AI") {
        targetLanguageMap = laraLanguages.target;
    } else if (provider === "Modern MT") {
        targetLanguageMap = modernMtLanguages.target;
    } else if (provider === "OpenAi") {
        targetLanguageMap = openAiLanguages.target;
    } else if (provider === "Gemini Pro") {
        targetLanguageMap = geminiProLanguages.target;
    } else if (provider === "Azure AI Translator") {
        targetLanguageMap = azureLanguages.target;
    } else if (provider === "Amazon Translate") {
        targetLanguageMap = amazonLanguages.target;
    } else if (provider === "Anthropic") {
        targetLanguageMap = anthropicLanguages.target;
    } else {
        targetLanguageMap = deepLLanguages.target;
    }
}

async function translate() {
    try {
        waiting = true;
        console.log("Preparing translation jobs...");
        statusMsg = m.preparing_translation_jobs();
        progress = 5;
        toast.push(m.starting_translation_jobs({ count: selectedTargetLanguages.length }), {
            classes: ["toast-info"]
        });

        // Save user preferences
        localStorage.setItem(
            "cc-translation-defaults",
            JSON.stringify({
                provider: provider,
                srcLanguage: srcLanguage,
                selectedTargetLanguages: selectedTargetLanguages,
                formatting: formatting
            })
        );

        // Create a copy of the source Event Group and remove any empty events
        console.log("Preparing source Event Group for translation");
        statusMsg = m.preparing_source_event_group();
        progress = 10;
        let copyOfEventGroup = JSON.parse(JSON.stringify(eventGroupState[eventGroupIndex]));
        copyOfEventGroup = removeTextlessEvents(copyOfEventGroup);
        
        // Upload source event group once
        const sourceFileId = uuidv4();
        console.log("Uploading source Event Group for translation");
        statusMsg = m.uploading_source_event_group();
        progress = 15;
        await uploadString(ref(storageRef, uploadBasePath + sourceFileId + ".json"), JSON.stringify(copyOfEventGroup));
        
        // Get the appropriate language maps
        let srcLanguageDisplay = "";
        if (provider === "Google Translate") {
            srcLanguageDisplay = googleLanguages.source[srcLanguage];
        } else if (provider === "Lara AI") {
            srcLanguageDisplay = laraLanguages.source[srcLanguage];
        } else if (provider === "Modern MT") {
            srcLanguageDisplay = modernMtLanguages.source[srcLanguage];
        } else if (provider === "OpenAi") {
            srcLanguageDisplay = openAiLanguages.source[srcLanguage];
        } else if (provider === "Gemini Pro") {
            srcLanguageDisplay = geminiProLanguages.source[srcLanguage];
        } else if (provider === "Azure AI Translator") {
            srcLanguageDisplay = azureLanguages.source[srcLanguage];
        } else if (provider === "Amazon Translate") {
            srcLanguageDisplay = amazonLanguages.source[srcLanguage];
        } else if (provider === "Anthropic") {
            srcLanguageDisplay = anthropicLanguages.source[srcLanguage];
        } else {
            srcLanguageDisplay = deepLLanguages.source[srcLanguage];
        }
        
        // Calculate total jobs and track completion
        totalJobs = selectedTargetLanguages.length;
        completedJobs = 0;
        let remainingTranslationSlots = await getTrialJobSlotsRemaining(homeRef, "translation");
        let limitedJobs = 0;
        
        // Submit one job for each target language
        for (const trgLanguage of selectedTargetLanguages) {
            const jobId = uuidv4();
            const isRtl = rtlLanguages.includes(trgLanguage);
            const trgLanguageDisplay = targetLanguageMap[trgLanguage];
            const jobDisplayName = `${eventGroupState[eventGroupIndex].name} (${trgLanguageDisplay})`;
            
            statusMsg = m.registering_job_for({ language: trgLanguageDisplay });
            console.log(`Registering translation job for ${trgLanguageDisplay}`);
            progress = 20 + Math.floor((completedJobs / totalJobs) * 60);
            
            // Create job info for this specific language
            const thisJobInfo = {
                id: jobId,
                teamId: teamId,
                owner: teamOwner,
                ownerId: teamOwnerId,
                projectId: projectState.id,
                projectName: projectState.name,
                submittedBy: userEmail,
                userId: userId,
                progress: 50,
                chars: 0,
                cost: 0,
                type: "translation",
                statusMsg: "Queued",
                status: "Submitted",
                createdOn: Timestamp.fromDate(new Date()),
                updatedOn: Timestamp.fromDate(new Date()),
                completedOn: null,
                deleted: false,
                config: {
                    displayName: jobDisplayName,
                    linkedGroup: eventGroupState[eventGroupIndex].id,
                    provider: provider,
                    srcLanguage: srcLanguage,
                    trgLanguage: trgLanguage,
                    srcLanguageDisplay: srcLanguageDisplay,
                    trgLanguageDisplay: trgLanguageDisplay,
                    originalFontFamily: styleState.fontFamily,
                    rtl: isRtl,
                    formatting: formatting,
                    sourceFileId: sourceFileId // Reference to the uploaded source file
                }
            };
            
            if (isTrialUser() && remainingTranslationSlots <= 0) {
                await writeTrialLimitJob(homeRef, thisJobInfo);
                limitedJobs++;
                completedJobs++;
                continue;
            }

            // Register job in Firestore
            await setDoc(doc(homeRef, jobId), thisJobInfo);
            
            // Trigger translation job
            await httpsCallable(functions, "v8TranslateProviderV6", { timeout: 540000 })(jobId);            
            completedJobs++;
            if (isTrialUser()) {
                remainingTranslationSlots--;
            }
        }
        
        statusMsg = m.all_translation_jobs_started({ count: totalJobs });
        progress = 100;
        
        if (limitedJobs > 0) {
            toast.push(TRIAL_LIMIT_MESSAGE, {
                classes: ["toast-danger"]
            });
        }
        if (limitedJobs < totalJobs) {
            toast.push(m.translation_jobs_started_toast({ count: totalJobs - limitedJobs }), {
                classes: ["toast-info"]
            });
        }

        showModal('aiTranslationImport');
    } catch (err) {
        console.log(err);
        errorHandler(err.message);
    }
}

function errorHandler(msg) {
    console.log(msg);
    waiting = false;
    
    toast.push(m.failed_to_start_translation_jobs(), {
        classes: ["toast-danger"]
    });
    
    showModal('aiTranslationImport');
}

function handleTargetLanguagesChange(event) {
    // Convert the HTMLSelectElement selectedOptions to an array of values
    const selectedOptions = Array.from(event.target.selectedOptions);
    selectedTargetLanguages = selectedOptions.map(option => option.value);
}

function toggleTargetLanguage(lang) {
    const index = selectedTargetLanguages.indexOf(lang);
    if (index === -1) {
        selectedTargetLanguages = [...selectedTargetLanguages, lang];
    } else {
        selectedTargetLanguages = selectedTargetLanguages.filter(l => l !== lang);
    }
}

function resetLanguages() {
    if (provider === "Lara AI") {
        srcLanguage = "en-US";
        selectedTargetLanguages = ["fr-FR"];
    } else {
        srcLanguage = "en";
        selectedTargetLanguages = ["fr"];
    }
    updateTargetLanguageMap();
}
</script>

<div 
    transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'automaticTranslation' ? 'show d-block' : ''}" 
    role="dialog" 
    tabindex="-1" 
    id="AutoTranslateModal"
    aria-labelledby="translateTitle"
    aria-describedby="translateDescription">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="translateTitle">{m.automatic_translation()}</h4>
                <button type="button" class="btn-close" aria-label={m.close_translation_modal()} onclick={hideModal}></button>
            </div>
            <div class="modal-body">
                <form onsubmit={preventDefault(translate)} aria-label={m.translation_settings()}>
                    <div class="mb-3">
                        <label class="form-label" for="serviceProvider">{m.service_provider()}</label>
                        <select class="form-select" 
                                id="serviceProvider"
                                bind:value={provider} 
                                onchange={() => {
                                    resetLanguages();
                                    updateTargetLanguageMap();
                                }} 
                                disabled={waiting}
                                aria-describedby="providerHelp">
                            <option>Amazon Translate</option>
                            <option>Anthropic</option>
                            <option>Azure AI Translator</option>
                            <option>DeepL</option>
                            <option>Gemini Pro</option>
                            <option>Google Translate</option> 
                            <option>Lara AI</option>
                            <option>Modern MT</option>
                            <option>OpenAi</option>
                        </select>
                        {#if provider === "Modern MT"}
                            <div id="providerHelp" class="alert alert-warning d-flex align-items-center small mt-2 mb-0" role="alert">
                                <i class="bi bi-exclamation-triangle-fill fs-5 me-2"></i>
                                <div>{m.modern_mt_sunset_warning()}</div>
                            </div>
                        {/if}
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="Linked group">{m.source_group()}</label>
                        <select class="form-select" bind:value={eventGroupIndex} disabled={waiting}>
                            {#each eventGroupState as eventGroup, index (index)}
                                <option value={index} selected={index === projectState.selected}>{eventGroup.name}</option>
                            {/each}
                        </select>
                    </div>
                    
                    <!-- Source Language - keeping the original structure -->
                    <div class="mb-3">
                        <label class="form-label" for="sourceLanguage">{m.source_language()}</label>
                        <select class="form-select" id="sourceLanguage" bind:value={srcLanguage} disabled={waiting}>
                            {#if provider === "Google Translate"}
                                {#each Object.entries(googleLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else if provider === "Anthropic"}
                                {#each Object.entries(anthropicLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else if provider === "Azure AI Translator"}
                                {#each Object.entries(azureLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else if provider === "Amazon Translate"}
                                {#each Object.entries(amazonLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else if provider === "Lara AI"}
                                {#each Object.entries(laraLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else if provider === "Modern MT"}
                                {#each Object.entries(modernMtLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else if provider === "Gemini Pro"}
                                {#each Object.entries(geminiProLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else if provider === "OpenAi"}
                                {#each Object.entries(openAiLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {:else} <!-- DeepL -->
                                {#each Object.entries(deepLLanguages.source) as languageOption (languageOption[0])}
                                <option value={languageOption[0]}>{languageOption[1]}</option>
                                {/each}
                            {/if}
                        </select>
                    </div>
                    
                    <!-- Target Languages - Standard multi-select -->
                    <div class="mb-3">
                        <label class="form-label" for="targetLanguages">{m.target_languages()} <span class="badge bg-primary">{m.selected_count({ count: selectedTargetLanguages.length })}</span></label>
                        <select 
                            class="form-select" 
                            id="targetLanguages" 
                            multiple 
                            size="8" 
                            onchange={handleTargetLanguagesChange}
                            disabled={waiting}
                            aria-describedby="targetLanguagesHelp"
                        >
                            {#each Object.entries(targetLanguageMap) as [langCode, langName] (langCode)}
                                <option 
                                    value={langCode} 
                                    selected={selectedTargetLanguages.includes(langCode)}
                                >
                                    {langName} {rtlLanguages.includes(langCode) ? '(RTL)' : ''}
                                </option>
                            {/each}
                        </select>
                        <small id="targetLanguagesHelp" class="form-text text-muted">
                            {m.hold_ctrl_or_command_to_select_multiple()}
                        </small>
                    </div>
                    
                    <!-- Selected languages summary - keep this for user feedback -->
                    {#if selectedTargetLanguages.length > 0}
                        <div class="mb-3">
                            <label for="selectedLanguages" class="form-label">{m.selected_languages()}:</label>
                            <div class="selected-languages" id="selectedLanguages">
                                {#each selectedTargetLanguages as langCode (langCode)}
                                    <span class="badge bg-primary me-1 mb-1">
                                        {targetLanguageMap[langCode]}
                                        <button type="button" class="btn-close btn-close-white ms-1" 
                                                style="font-size: 0.5rem;" aria-label={m.remove_language({ language: targetLanguageMap[langCode] })} 
                                                onclick={() => toggleTargetLanguage(langCode)} disabled={waiting}></button>
                                    </span>
                                {/each}
                            </div>
                        </div>
                    {/if}

                    <hr />                    
                    <div class="form-check form-switch">
                        <input type="checkbox" class="form-check-input" id="formattingSwitch" bind:checked={formatting} disabled={waiting} />
                        <label class="form-check-label" for="formattingSwitch">{m.include_formatting()}</label>
                    </div>
                </form>
                <button class="btn btn-primary float-end" type="button" 
                        disabled={waiting || !srcLanguage || selectedTargetLanguages.length === 0} 
                        onclick={translate}>
                    {selectedTargetLanguages.length !== 1 ? m.submit_translation_jobs({ count: selectedTargetLanguages.length }) : m.submit_translation_job({ count: selectedTargetLanguages.length })}
                </button>
                {#if waiting}
                <div class="mt-3" role="status" aria-live="polite">
                    <BarLoader size="150" color="#1eb4b2" unit="px" duration="3s" />
                    <p class="small text-muted">
                        {statusMsg} ({progress}%)
                    </p>
                    {#if totalJobs > 1}
                    <p class="small text-muted">
                        {m.processing_job_of({ completed: completedJobs, total: totalJobs })}
                    </p>
                    {/if}
                </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
.selected-languages {
    display: flex;
    flex-wrap: wrap;
}

/* Improve multi-select styling */
select[multiple] {
    height: auto;
    min-height: 200px;
}
</style>